import { useEffect, useState, useMemo } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DispatchOrderModal from "./DispatchOrderModal";
import CancelOrderModal from "./CancelOrderModal";
import { globalAlert, globalLoader } from "../../../actions/commonActions";
import { formatDate, formatDateTime } from "../../../i18n/dateLocale";
import { logOut } from "../../../_shared/commonFunction";
import { adminOnlineOrderCancelhApi, adminOnlineOrderDispatchApi } from "../../../admin/_actions";
// Font Awesome sort icons are included via CDN in public/index.html

const PendingOrderTable = ({orders, callBasicApi, history}) => {
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'transactionTime', direction: 'desc' });
    const { t } = useTranslation();

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    const handleOpenModel = (order) => {
        const now = new Date();
        const hours = String(now?.getHours())?.padStart(2, '0');
        const minutes = String(now?.getMinutes())?.padStart(2, '0');
        setSelectedOrder(order);
        setDispatchDate(now?.toISOString()?.split("T")?.[0]);
        setDispatchTime(`${hours}:${minutes}`);
        setIsShowModel(true);
    };

    const handleCancelModel = (order) => {
        setSelectedOrder(order);
        setShowCancel(true);
    };

    const [dispatchDate, setDispatchDate] = useState("");
    const [dispatchTime, setDispatchTime] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!dispatchDate || !dispatchTime) {
                globalAlert("error", "Please select both date and time");
                return;
            }

            const dispatchDateTime = new Date(`${dispatchDate}T${dispatchTime}`);
            
            if (isNaN(dispatchDateTime.getTime())) {
                globalAlert("error", "Invalid date/time format");
                return;
            }

            globalLoader(true);
            const res = await adminOnlineOrderDispatchApi(selectedOrder?.userTransactionId, { dispatchDateTime });
            
            if (res?.data?.status === 200) {
                globalAlert("success", res?.data?.message);
                await callBasicApi();
                setIsShowModel(false);
                setSelectedOrder(null);
            } else {
                globalAlert("error", res?.data?.message);
            }
        } catch (error) {
            globalAlert("error", error?.response?.data?.message || "Failed to dispatch order");
            if (error?.response?.data?.status === 401) {
                logOut(history, "/admin/login");
            }
        } finally {
            globalLoader(false);
        }
    };

    const handleCancelConfirm = async () => {
        try {
            if (!cancelReason) {
                globalAlert("error", "Please provide a cancellation reason");
                return;
            }

            globalLoader(true);
            const res = await adminOnlineOrderCancelhApi(selectedOrder?.userTransactionId, { cancelReason });
            
            if (res?.data?.status === 200) {
                globalAlert("success", res?.data?.message);
                await callBasicApi();
                setShowCancel(false);
                setSelectedOrder(null);
                setCancelReason("");
            } else {
                globalAlert("error", res?.data?.message);
            }
        } catch (error) {
            globalAlert("error", error?.response?.data?.message || "Failed to cancel order");
            if (error?.response?.data?.status === 401) {
                logOut(history, "/admin/login");
            }
        } finally {
            globalLoader(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedOrders = useMemo(() => {
        if (!orders) return [];
        
        return [...orders].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'userTransactionId':
                    aValue = a.userTransactionId || '';
                    bValue = b.userTransactionId || '';
                    break;
                case 'phone':
                    aValue = a.onlineStoreAddress?.phone || '';
                    bValue = b.onlineStoreAddress?.phone || '';
                    break;
                case 'postalCode':
                    aValue = a.onlineStoreAddress?.postalCode || '';
                    bValue = b.onlineStoreAddress?.postalCode || '';
                    break;
                case 'productName':
                    aValue = a.product?.name || '';
                    bValue = b.product?.name || '';
                    break;
                case 'transactionTime':
                    aValue = new Date(a.transactionTime);
                    bValue = new Date(b.transactionTime);
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [orders, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="fas fa-sort ms-1" />;
        return sortConfig.direction === 'asc' ? 
            <i className="fas fa-sort-up ms-1" /> : 
            <i className="fas fa-sort-down ms-1" />;
    };

    const [isShowModel, setIsShowModel] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    useEffect(() => {
        const handleClickOutside = () => {
            if (openMenuIndex !== null) setOpenMenuIndex(null);
        };
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, [openMenuIndex]);

    return (
        <>
        {showCancel && <CancelOrderModal
        show={showCancel}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancelConfirm}
        reason={cancelReason}
        setReason={setCancelReason}
      />}
       {isShowModel && <DispatchOrderModal
        show={isShowModel}
        onClose={() => setIsShowModel(false)}
        onSubmit={handleSubmit}
        date={dispatchDate}
        time={dispatchTime}
        setDate={setDispatchDate}
        setTime={setDispatchTime}
      />}
            <Table bordered hover className="table-orders">
                <thead>
                    <tr>
                        <th>{t("common.serialNo")}</th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('userTransactionId')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.orderId")}
                            {getSortIcon('userTransactionId')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('phone')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.userPhone")}
                            {getSortIcon('phone')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('postalCode')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.postcode")}
                            {getSortIcon('postalCode')}
                        </th>
                        <th>{t("admin.orders.table.address")}</th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('productName')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.productName")}
                            {getSortIcon('productName')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('transactionTime')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.orderDate")}
                            {getSortIcon('transactionTime')}
                        </th>
                        <th>{t("admin.orders.table.orderTime")}</th>
                        <th>{t("common.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedOrders?.map((order, idx) => (
                        <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{order?.userTransactionId}</td>
                            <td>{order?.onlineStoreAddress?.phone}</td>
                            <td>{order?.onlineStoreAddress?.postalCode}</td>
                            <td>{t("common.street")}: {order?.onlineStoreAddress?.street || t("common.na")}, {t("common.address")}: {order?.onlineStoreAddress?.address || t("common.na")}, {t("common.city")}: {order?.onlineStoreAddress?.city || t("common.na")}</td>
                            <td>{order?.product?.name}</td>
                            <td>{formatDate(new Date(order?.transactionTime))}</td>
                            <td>{formatDate(new Date(order?.transactionTime), "p")}</td>
                            <td className="position-relative text-center">
                                <div className="dropdown-wrapper" onClick={(e) => e.stopPropagation()}>
                                    <i
                                        className="fa fa-ellipsis-v cursor-pointer"
                                        onClick={() => toggleMenu(idx)}
                                    ></i>

                                    {openMenuIndex === idx && (
                                        <div className="action-dropdown">
                                            <div className="action-item " onClick={() => handleOpenModel(order)}><i className="fa fa-solid fa-circle mr-2" style={{ fontSize: '6px' }}></i>{t("admin.orders.dispatch")}</div>
                                            <div className="action-item"  onClick={() => handleCancelModel(order)}><i className="fa fa-solid fa-circle mr-2" style={{ fontSize: '6px' }}></i>{t("common.cancel")}</div>
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    )
};

export default PendingOrderTable;